import type { ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { UnknownAction } from 'redux';
import { z } from 'zod';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_INTERAGENCY_CHAT,
  SOURCE_RCS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_TRANSFER,
  CONVERSATION_TRANSFER_UPDATE,
  CONVERSATION_UPDATE,
} from '@rsos/hermes-js/src/typings/conversation';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import useIsTT911Enabled from '@rsos/hooks/useIsTT911Enabled';
import { useCurrentPSAPAccountID } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import {
  addConversation,
  applyConversationUpdate,
  applyReceivedTransfer,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { invalidateTCAPIConversationMetadata } from '@rsos/reduxDataStore/src/features/conversations/tcapiAPI';
import { setOutboundSMSLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { addRCSConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { useLazyGetConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import { selectUserOrgByApp } from '@rsos/sinatra';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { dismissToastByID } from '@rsos/utils/toastUtils';
import { addLocToQueue } from '../../../actions';
import { RootState } from '../../../app';
import { BODY_SOURCE__OUTBOUND_SMS } from '../../../constants/hermes';
import useHasAccessToAdminLineRequests from '../../../hooks/useHasAccessToAdminLineRequests';
import { invalidateInteragencyConversationList } from '../../../reduxDataStore/interagency/interagencyAPI';
import { logMissingTT911Capability } from '../../../utils/logMissingTT911Capability';

// Shared body shape for call-queue lifecycle messages (start, update, end).
const callQueueBodySchema = z
  .object({
    source: z.enum([SOURCE_ADMIN_SMS, SOURCE_RCS, SOURCE_TCAPI]),
    application: z.string(),
    caller_id: z.string().nullable(),
    conversation_start_time: z.number(),
    conversation_id: z.string(),
    valid_for_psap: z.boolean().nullable(),
    extra_info: z.record(z.string(), z.unknown()).default({}),
    location_data: z.unknown().optional(),
    session_metadata: z.unknown().optional(),
  })
  .passthrough();

const interagencyBodySchema = z
  .object({
    source: z.literal(SOURCE_INTERAGENCY_CHAT),
    application: z.string(),
    conversation_id: z.string(),
    conversation_name: z.string(),
    conversation_start_time: z.number(),
    caller_id: z.string().nullable(),
    valid_for_psap: z.boolean().nullable(),
    /**
     * @deprecated Contains nested participants, status, timestamps etc.
     * When building interagency chat features, elevate the fields you
     * need to the top level of this schema with proper zod types instead
     * of digging into this untyped bag.
     */
    extra_info: z.unknown(),
  })
  .passthrough();

// TF911 outbound SMS lifecycle messages. Distinct from callQueueBodySchema
// because outbound SMS is read from its own (textFrom911) slice and is keyed
// off `conversation_start_time` rather than carrying location_data.
const outboundSmsBodySchema = z
  .object({
    source: z.literal(BODY_SOURCE__OUTBOUND_SMS),
    application: z.string(),
    caller_id: z.string(),
    conversation_id: z.string(),
    conversation_start_time: z.number(),
    valid_for_psap: z.boolean().nullable().optional(),
    extra_info: z.record(z.string(), z.unknown()).default({}),
  })
  .passthrough();

const lifecycleBodySchema = z.union([
  callQueueBodySchema,
  interagencyBodySchema,
  outboundSmsBodySchema,
]);

// Transfer messages are a "session changed, refetch" trigger only — the
// frontend no longer relies on the WS body for transfer state (the TCAPI
// session GET response carries it). The schema only captures what's needed
// to route: caller_id, plus receiving authority for the queue-add
// side-effect on the receiver side.
const transferBodySchema = z
  .object({
    caller_id: z.string(),
    receiving_authority_account_id: z.string().optional(),
  })
  .passthrough();

// z.discriminatedUnion matches exactly one branch based on the literal
// value of the 'type' field. If the incoming message's type doesn't
// match any branch, parsing fails — so only the message types listed
// here are handled.
const conversationMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal(CONVERSATION_START), body: lifecycleBodySchema }),
  z.object({ type: z.literal(CONVERSATION_UPDATE), body: lifecycleBodySchema }),
  z.object({ type: z.literal(CONVERSATION_END), body: lifecycleBodySchema }),
  z.object({
    type: z.literal(CONVERSATION_TRANSFER),
    body: transferBodySchema,
  }),
  z.object({
    type: z.literal(CONVERSATION_TRANSFER_UPDATE),
    body: transferBodySchema,
  }),
]);

type ConversationWsMessage = z.infer<typeof conversationMessageSchema>;

function isTransferMessage(
  msg: ConversationWsMessage,
): msg is Extract<
  ConversationWsMessage,
  { type: typeof CONVERSATION_TRANSFER | typeof CONVERSATION_TRANSFER_UPDATE }
> {
  return (
    msg.type === CONVERSATION_TRANSFER ||
    msg.type === CONVERSATION_TRANSFER_UPDATE
  );
}

const normalizeLocation = (body: z.infer<typeof callQueueBodySchema>) => {
  const baseLocation = {
    caller_id: body.caller_id,
    call_start_time: body.conversation_start_time,
    source: body.source,
    application: body.application,
  };

  const locationData = body.location_data;
  // Only spread location_data when it carries valid coordinates; lifecycle
  // messages can include location_data with null lat/lng and a zero
  // location_time, and spreading those would produce an epoch timestamp.
  if (
    locationData &&
    typeof locationData === 'object' &&
    isValidCoordinates(locationData)
  ) {
    return { ...baseLocation, ...locationData };
  }

  return baseLocation;
};

const useHandleConversationMessage = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  // Typed so that thunks (e.g. applyConversationUpdate) can be dispatched
  // alongside plain actions.
  const dispatch =
    useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
  const hasAdminLineRequests = useHasAccessToAdminLineRequests();
  const currentPSAPAccountId = useCurrentPSAPAccountID();
  const isTT911Enabled = useIsTT911Enabled();
  const isTF911Enabled = useIsTF911Enabled();
  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application ?? '',
      )?.name,
  );
  const [triggerGetConversationMetadata] =
    useLazyGetConversationMetadataQuery();
  const handleConversationMessage = useCallback(
    // StreamWS callback receives untyped messages; zod validates below
    (message: Record<string, unknown>) => {
      const parsed = conversationMessageSchema.safeParse(message);
      if (!parsed.success) {
        return;
      }
      const msg = parsed.data;

      if (msg.body.source === SOURCE_ADMIN_SMS && !hasAdminLineRequests) {
        return;
      }

      if (isTransferMessage(msg)) {
        const isReceivingAgency = Boolean(
          msg.body.receiving_authority_account_id &&
            currentPSAPAccountId &&
            msg.body.receiving_authority_account_id === currentPSAPAccountId,
        );

        if (isReceivingAgency) {
          // Transfers in this handler are always TCAPI — drop and log if
          // the receiving PSAP can't service them.
          if (!isTT911Enabled) {
            logMissingTT911Capability({
              callSite: 'useHandleConversationMessage:transfer',
              callerId: msg.body.caller_id,
              source: SOURCE_TCAPI,
              extra: { messageType: msg.type },
            });
            return;
          }
          dispatch(
            addLocToQueue({
              caller_id: msg.body.caller_id,
              source: SOURCE_TCAPI,
              call_start_time: Date.now(),
            }),
          );
          dispatch(applyReceivedTransfer(msg.body.caller_id));
        } else {
          dispatch(invalidateTCAPIConversationMetadata(msg.body.caller_id));
        }
        return;
      }

      // Drop TCAPI lifecycle messages when the PSAP lacks the capability so
      // we don't populate the call queue or conversation slice with an
      // entry the operator can't act on. Mirrors the suppression at
      // Hermes/StreamWS handleLocations and the toast hook.
      if (msg.body.source === SOURCE_TCAPI && !isTT911Enabled) {
        logMissingTT911Capability({
          callSite: 'useHandleConversationMessage',
          callerId: msg.body.caller_id,
          source: SOURCE_TCAPI,
          extra: { messageType: msg.type },
        });
        return;
      }

      if (msg.body.source === SOURCE_INTERAGENCY_CHAT) {
        // TODO(SOUP-1112): Handle interagency chat lifecycle messages
        // eslint-disable-next-line no-console -- debug logging for interagency chat development
        console.debug('Received interagency chat message:', msg);

        // Force refetch of the conversations list via tag invalidation
        dispatch(invalidateInteragencyConversationList());

        return;
      }

      // TF911 outbound SMS is read from its own (textFrom911) slice and gets a
      // dedicated queue entry, so it must not fall through to the TCAPI
      // `conversations` slice handling below. The WS message is only a trigger:
      // we refetch the conversation via RTK Query so the authoritative API
      // response populates the TF911 slice (its getConversationMetadata
      // matchFulfilled reducer), rather than writing slice state from the WS
      // body. START additionally adds the caller to the call queue.
      if (msg.body.source === BODY_SOURCE__OUTBOUND_SMS) {
        if (!isTF911Enabled) {
          return;
        }
        if (msg.type === CONVERSATION_START) {
          dispatch(
            setOutboundSMSLocation({
              caller_id: msg.body.caller_id,
              call_start_time: msg.body.conversation_start_time,
            }),
          );
        }
        if (msg.type === CONVERSATION_START || msg.type === CONVERSATION_END) {
          // END returns the ended status in the metadata response, which is
          // what the start/reuse guard reads.
          triggerGetConversationMetadata({
            callerID: msg.body.caller_id,
            orgName,
            custom: { isOutboundSMS: true },
          });
        }
        return;
      }

      // At this point source is narrowed to a call-queue source (admin_sms,
      // rcs, tcapi) and transfers/interagency have already returned above.
      // caller_id should always be present for these sources.
      const callQueueSources = [SOURCE_ADMIN_SMS, SOURCE_RCS, SOURCE_TCAPI];
      if (!msg.body.caller_id || !callQueueSources.includes(msg.body.source)) {
        // eslint-disable-next-line no-console -- Defensive check: if we reach here, the narrowing logic above has a gap
        console.error(
          'Unexpected state in conversation handler: expected a call-queue source with caller_id but got',
          { source: msg.body.source, caller_id: msg.body.caller_id },
        );
        return;
      }
      // Only update the location queue when the message carries real coordinates,
      // or on CONVERSATION_START (to add the caller to the queue on first contact).
      // UPDATE/END without coordinates have nothing new to contribute; dispatching
      // them appends locationless entries that can produce an epoch timestamp.
      const location = normalizeLocation(msg.body);
      if (
        isValidCoordinates(
          location as {
            latitude?: number | string | null;
            longitude?: number | string | null;
          },
        ) ||
        msg.type === CONVERSATION_START
      ) {
        dispatch(addLocToQueue(location));
      }

      let payload = msg.body;
      if (
        msg.body.source === SOURCE_TCAPI &&
        msg.type === CONVERSATION_UPDATE
      ) {
        dismissToastByID(`conversations-toast-${msg.body.caller_id}`);
      }

      if (msg.body.source === SOURCE_TCAPI) {
        payload = { ...msg.body, ...msg.body.extra_info };
      }

      if (msg.body.source === SOURCE_RCS && msg.body.session_metadata) {
        dispatch(addRCSConversation(msg.body.session_metadata));
      } else if (msg.type === CONVERSATION_START) {
        dispatch(addConversation(payload));
      } else if (
        msg.type === CONVERSATION_UPDATE ||
        msg.type === CONVERSATION_END
      ) {
        dispatch(
          applyConversationUpdate({ body: payload, messageType: msg.type }),
        );
      }
    },
    [
      dispatch,
      hasAdminLineRequests,
      currentPSAPAccountId,
      isTT911Enabled,
      isTF911Enabled,
      orgName,
      triggerGetConversationMetadata,
    ],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleConversationMessage,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleConversationMessage');
      };
    }
  }, [StreamWS, handleConversationMessage]);
};

export default useHandleConversationMessage;
